import { useState, useEffect } from "react";
import {
  Route,
  Routes,
  Link,
  Outlet,
  useParams,
  useLocation,
} from "react-router-dom";
import { Button } from "@/components/ui";
import { LikedPosts, Saved } from "@/_root/pages";
import { useUserContext } from "@/context/AuthContext";
import { 
  useCheckFollowStatus, 
  useFollowUser, 
  useGetFollowersCount, 
  useGetFollowingCount, 
  useGetUserById, 
  useGetUserPosts, 
  useUnfollowUser 
} from "@/lib/react-query/queries";
import { GridPostList, Loader } from "@/components/shared";
import { Dialog, DialogContent, DialogTitle, DialogOverlay, DialogPortal } from "@radix-ui/react-dialog";
import { DialogHeader } from "@/components/ui/dialog";
import Followers from "./Followers";
import Following from "./Following";
import { toast } from "@/components/ui/use-toast";
import { motion, AnimatePresence } from "framer-motion";

interface StatBlockProps {
  value: string | number;
  label: string;
  onClick?: () => void;
  isClickable?: boolean;
}

const StatBlock = ({ value, label, onClick, isClickable }: StatBlockProps) => (
  <motion.div 
    className={`flex-center gap-2 ${isClickable ? 'cursor-pointer hover:bg-dark-4 p-3 rounded-lg transition-all duration-200 hover:scale-105' : 'p-3'}`}
    onClick={onClick}
    whileHover={isClickable ? { scale: 1.05 } : {}}
    whileTap={isClickable ? { scale: 0.95 } : {}}
  >
    <p className="small-semibold lg:body-bold text-primary-500 font-bold">{value}</p>
    <p className="small-medium lg:base-medium text-light-2">{label}</p>
  </motion.div>
);

const Profile = () => {
  const { id } = useParams();
  const { user } = useUserContext();
  const { pathname } = useLocation();
  
  // Local state for UI management
  const [openFollowersDialog, setOpenFollowersDialog] = useState(false);
  const [openFollowingDialog, setOpenFollowingDialog] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [localFollowStatus, setLocalFollowStatus] = useState<any>(null);
  const [localFollowersCount, setLocalFollowersCount] = useState<number>(0);

  // Data fetching hooks
  const { data: currentUser, isLoading: isUserLoading } = useGetUserById(id || "");
  const { data: followersCount, refetch: refetchFollowersCount } = useGetFollowersCount(id || "");
  const { data: followingCount } = useGetFollowingCount(id || "");
  const { data: followStatus, refetch: refetchFollowStatus } = useCheckFollowStatus(user.id, id || "");
  const { data: userPostsData} = useGetUserPosts(id || "");
  
  // Mutation hooks
  const { mutate: followUser } = useFollowUser({
    onSuccess: () => {
      setIsFollowLoading(false);
      toast({
        title: "Success",
        description: `You are now following ${currentUser?.name}`,
      });
      refetchFollowStatus();
      refetchFollowersCount();
    },
    onError: (error) => {
      setIsFollowLoading(false);
      setLocalFollowStatus(followStatus);
      setLocalFollowersCount(followersCount || 0);
      toast({
        title: "Error",
        description: "Failed to follow user. Please try again.",
        variant: "destructive",
      });
      console.error("Follow error:", error);
    },
  });

  const { mutate: unfollowUser } = useUnfollowUser({
    onSuccess: () => {
      setIsFollowLoading(false);
      toast({
        title: "Success",
        description: `You unfollowed ${currentUser?.name}`,
      });
      refetchFollowStatus();
      refetchFollowersCount();
    },
    onError: (error) => {
      setIsFollowLoading(false);
      setLocalFollowStatus(followStatus);
      setLocalFollowersCount(followersCount || 0);
      toast({
        title: "Error",
        description: "Failed to unfollow user. Please try again.",
        variant: "destructive",
      });
      console.error("Unfollow error:", error);
    },
  });

  // Update local state when server data changes
  useEffect(() => {
    if (followStatus !== undefined) {
      setLocalFollowStatus(followStatus);
    }
  }, [followStatus]);

  useEffect(() => {
    if (followersCount !== undefined) {
      setLocalFollowersCount(followersCount);
    }
  }, [followersCount]);

  // Follow/unfollow handler
  const handleFollow = async () => {
    if (!currentUser || !user.id || user.id === currentUser.$id) return;
    
    setIsFollowLoading(true);
    const isCurrentlyFollowing = !!localFollowStatus;
    
    try {
      if (isCurrentlyFollowing && localFollowStatus?.$id) {
        unfollowUser(localFollowStatus.$id);
      } else {
        followUser({ 
          followerId: user.id, 
          followingId: currentUser.$id 
        });
      }
    } catch (error) {
      console.error("Follow/Unfollow action failed:", error);
      setIsFollowLoading(false);
    }
  };

  // Loading state
  if (isUserLoading || !currentUser) {
    return (
      <div className="flex-center w-full h-full">
        <Loader />
      </div>
    );
  }

  const isOwnProfile = user.id === currentUser.$id;
  const isFollowing = !!localFollowStatus;

  return (
    <motion.div 
      className="profile-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="profile-inner_container">
        <div className="flex xl:flex-row flex-col max-xl:items-center flex-1 gap-7">
          {/* Profile Image */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
            className="relative"
          >
            <img
              src={currentUser.imageUrl || "/assets/icons/profile-placeholder.svg"}
              alt="profile"
              className="w-28 h-28 lg:h-36 lg:w-36 rounded-full object-cover border-4 border-primary-500/20 shadow-lg"
            />
            <div className="absolute inset-0 rounded-full bg-gradient-to-t from-black/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
          </motion.div>

          <div className="flex flex-col flex-1 justify-between md:mt-2">
            <div className="flex flex-col w-full">
              <motion.h1 
                className="text-center xl:text-left h3-bold md:h1-semibold w-full bg-gradient-to-r from-primary-500 to-purple-500 bg-clip-text text-transparent"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                {currentUser.name}
              </motion.h1>
              <motion.p 
                className="small-regular md:body-medium text-light-3 text-center xl:text-left opacity-80"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                @{currentUser.username}
              </motion.p>
            </div>

            {/* Stats */}
            <motion.div 
              className="flex gap-4 mt-10 items-center justify-center xl:justify-start flex-wrap z-20 bg-dark-4/30 rounded-xl p-4 backdrop-blur-sm border border-dark-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <StatBlock value={userPostsData?.documents?.length || 0} label="Posts" />
              <div className="w-px h-8 bg-dark-3" />
              <StatBlock 
                value={localFollowersCount} 
                label="Followers" 
                onClick={() => setOpenFollowersDialog(true)}
                isClickable={true}
              />
              <div className="w-px h-8 bg-dark-3" />
              <StatBlock 
                value={followingCount || 0} 
                label="Following" 
                onClick={() => setOpenFollowingDialog(true)}
                isClickable={true}
              />
            </motion.div>

            <motion.p 
              className="small-medium md:base-medium text-center xl:text-left mt-7 max-w-screen-sm text-light-2 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {currentUser.bio}
            </motion.p>
          </div>

          {/* Action Buttons */}
          <motion.div 
            className="flex justify-center gap-4 flex-wrap"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
          >
            {/* Edit Profile Button - Only for own profile */}
            {isOwnProfile && (
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to={`/update-profile/${currentUser.$id}`}
                  className="h-12 bg-gradient-to-r from-dark-4 to-dark-3 px-6 text-light-1 flex-center gap-2 rounded-xl hover:from-dark-3 hover:to-dark-2 transition-all duration-300 shadow-md border border-dark-3"
                >
                  <img
                    src="/assets/icons/edit.svg"
                    alt="edit"
                    width={20}
                    height={20}
                  />
                  <p className="flex whitespace-nowrap small-medium font-semibold">
                    Edit Profile
                  </p>
                </Link>
              </motion.div>
            )}

            {/* Follow Button - Only for other users' profiles */}
            {!isOwnProfile && (
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button 
                  type="button" 
                  className={`px-8 min-w-[120px] transition-all duration-300 shadow-md group ${
                    isFollowing 
                      ? "shad-button_ghost hover:bg-rose-600 hover:text-white border border-rose-500/30" 
                      : "shad-button_primary bg-gradient-to-r from-primary-500 to-purple-600 hover:from-primary-600 hover:to-purple-700"
                  }`}
                  onClick={handleFollow}
                  disabled={isFollowLoading}
                >
                  <AnimatePresence mode="wait">
                    {isFollowLoading ? (
                      <motion.div 
                        key="loading"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2"
                      >
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        {isFollowing ? "Unfollowing..." : "Following..."}
                      </motion.div>
                    ) : (
                      <motion.span
                        key="text"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="font-semibold"
                      >
                        {isFollowing ? (
                          <>
                            <span className="group-hover:hidden">Following</span>
                            <span className="hidden group-hover:inline">Unfollow</span>
                          </>
                        ) : (
                          "Follow"
                        )}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>
              </motion.div>
            )}

            {/* Saved Posts Link - Only for own profile */}
            {isOwnProfile && (
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to={`/profile/${id}/saved`}
                  className={`profile-tab rounded-xl transition-all duration-300 shadow-md border border-dark-4 ${
                    pathname === `/profile/${id}/saved` && "!bg-dark-3 border-primary-500/30"
                  }`}
                >
                  <img
                    src="/assets/icons/saved.svg"
                    alt="saved"
                    width={20}
                    height={20}
                  />
                  <span className="font-semibold">Saved Posts</span>
                </Link>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Followers Dialog */}
      <Dialog open={openFollowersDialog} onOpenChange={setOpenFollowersDialog}>
        <DialogPortal>
          <DialogOverlay className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogContent className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-[450px] max-h-[85vh] translate-x-[-50%] translate-y-[-50%] gap-0 overflow-hidden rounded-xl border border-dark-4 bg-gradient-to-br from-dark-1 to-dark-2 shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
            <DialogHeader className="border-b border-dark-4/50 p-6 bg-gradient-to-r from-primary-500/10 to-purple-500/10">
              <DialogTitle className="text-xl font-bold bg-gradient-to-r from-primary-500 to-purple-500 bg-clip-text text-transparent flex items-center gap-3">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-8 h-8 bg-gradient-to-r from-primary-500 to-purple-500 rounded-full flex items-center justify-center"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </motion.div>
                Followers ({localFollowersCount})
              </DialogTitle>
            </DialogHeader>
            <Followers userId={id || ""} showTrustScore={true} />
          </DialogContent>
        </DialogPortal>
      </Dialog>

      {/* Following Dialog */}
      <Dialog open={openFollowingDialog} onOpenChange={setOpenFollowingDialog}>
        <DialogPortal>
          <DialogOverlay className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogContent className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-[450px] max-h-[85vh] translate-x-[-50%] translate-y-[-50%] gap-0 overflow-hidden rounded-xl border border-dark-4 bg-gradient-to-br from-dark-1 to-dark-2 shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
            <DialogHeader className="border-b border-dark-4/50 p-6 bg-gradient-to-r from-primary-500/10 to-purple-500/10">
              <DialogTitle className="text-xl font-bold bg-gradient-to-r from-primary-500 to-purple-500 bg-clip-text text-transparent flex items-center gap-3">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-8 h-8 bg-gradient-to-r from-primary-500 to-purple-500 rounded-full flex items-center justify-center"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </motion.div>
                Following ({followingCount || 0})
              </DialogTitle>
            </DialogHeader>
            <Following userId={id || ""} showTrustScore={true} />
          </DialogContent>
        </DialogPortal>
      </Dialog>

      {/* Profile Navigation Tabs - Only for own profile */}
      {isOwnProfile && (
        <motion.div 
          className="flex max-w-5xl w-full gap-1 bg-dark-4/30 p-1 rounded-xl backdrop-blur-sm border border-dark-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <Link
            to={`/profile/${id}`}
            className={`profile-tab flex-1 rounded-lg transition-all duration-300 hover:bg-dark-3 ${
              pathname === `/profile/${id}` && "!bg-dark-3 border border-primary-500/30 shadow-md"
            }`}
          >
            <img
              src="/assets/icons/posts.svg"
              alt="posts"
              width={20}
              height={20}
            />
            <span className="font-semibold">Posts</span>
          </Link>
          <Link
            to={`/profile/${id}/liked-posts`}
            className={`profile-tab flex-1 rounded-lg transition-all duration-300 hover:bg-dark-3 ${
              pathname === `/profile/${id}/liked-posts` && "!bg-dark-3 border border-primary-500/30 shadow-md"
            }`}
          >
            <img
              src="/assets/icons/like.svg"
              alt="like"
              width={20}
              height={20}
            />
            <span className="font-semibold">Liked Posts</span>
          </Link>
        </motion.div>
      )}

      {/* Routes */}
      <Routes>
          <Route
            index
            element={
                  <GridPostList 
                    posts={userPostsData?.documents || []} 
                    showUser={false} 
                    showStats={true} // ✅ Show stats for profile posts
                  />
            }
          />
          {isOwnProfile && (
            <>
              <Route path="/liked-posts" element={<LikedPosts />} />
              <Route path="/saved" element={<Saved />} />
            </>
          )}
      </Routes>

      <Outlet />
    </motion.div>
  );
};

export default Profile;